import React, { useEffect, useRef, useState } from 'react';
import { useDrag, useDragLayer, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

import clsx from 'clsx';

import { AvatarSize } from '../../../shared/constants';
import { UiAlpha } from '../../../styles/ui-alpha';
import { IExercise } from '../../../types/interfaces/exercise';
import { ITrainingSet } from '../../../types/interfaces/training-set';
import { getSetKey } from '../../../utils/training/set-utils';
import {
  blocksToOrderPayload,
  mergeBlocks,
  mergeRowIntoBlockEnd,
  moveBlockBetween,
  moveRowToBetweenBlocks,
  moveRowToBlock,
  trainingSetsToBlocks,
} from '../../../utils/training/training-sets-sort-block-utils';
import AppExerciseAvatar from '../../designe/app-entity-avatar/app-exercise-avatar/AppExerciseAvatar';
import AppButton, {
  AppCancelButton,
} from '../../designe/buttons/app-button/AppButton';
import AppDialog from '../app-dialog/AppDialog';
import { useStyles } from './SortTrainingSetsModal.styles';

export const TRAINING_SET_SORT_ROW = 'TRAINING_SET_SORT_ROW';
export const TRAINING_SET_SORT_BLOCK = 'TRAINING_SET_SORT_BLOCK';

interface IRowDragItem {
  type: typeof TRAINING_SET_SORT_ROW;
  blockIndex: number;
  rowIndex: number;
  previewTitle: string;
  previewExercise?: IExercise;
  previewWidth?: number;
}

interface IBlockDragItem {
  type: typeof TRAINING_SET_SORT_BLOCK;
  blockIndex: number;
  previewLabel: string;
  previewWidth?: number;
}

interface ISortTrainingSetsModalProps {
  open: boolean;
  onClose: () => void;
  trainingSets: ITrainingSet[];
  onSave: (
    sets: { id: string; setNumber: number; orderNumber: number }[],
  ) => Promise<void>;
  isSaving: boolean;
}

function DropBetweenBlocks(props: {
  insertBeforeBlockIndex: number;
  setBlocks: React.Dispatch<React.SetStateAction<ITrainingSet[][]>>;
  dragActive: boolean;
}) {
  const { insertBeforeBlockIndex, setBlocks, dragActive } = props;
  const { classes } = useStyles();
  const theme = useTheme();
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: [TRAINING_SET_SORT_ROW, TRAINING_SET_SORT_BLOCK],
      drop: (item: IRowDragItem | IBlockDragItem) => {
        setBlocks((prev) => {
          if (item.type === TRAINING_SET_SORT_ROW) {
            return moveRowToBetweenBlocks(
              prev,
              item.blockIndex,
              item.rowIndex,
              insertBeforeBlockIndex,
            );
          }
          return moveBlockBetween(
            prev,
            item.blockIndex,
            insertBeforeBlockIndex,
          );
        });
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    }),
    [insertBeforeBlockIndex, setBlocks],
  );

  const showTarget = dragActive && canDrop;
  const isHover = dragActive && isOver && canDrop;

  const innerHeight = !dragActive ? 4 : !showTarget ? 4 : isHover ? 56 : 36;

  return (
    <Box className={classes.dropZoneOuter}>
      <Box
        ref={drop as unknown as React.Ref<HTMLDivElement>}
        className={classes.dropZoneInner}
        sx={{
          minHeight: innerHeight,
          border: showTarget
            ? `2px dashed ${alpha(
                theme.palette.primary.main,
                isHover ? 0.92 : 0.58,
              )}`
            : 'none',
          bgcolor:
            showTarget && isHover
              ? alpha(theme.palette.primary.main, UiAlpha.Tint)
              : showTarget
                ? alpha(theme.palette.primary.main, UiAlpha.Subtle)
                : 'transparent',
        }}
      />
    </Box>
  );
}

function DropBeforeRow(props: {
  blockIndex: number;
  insertBeforeRowIndex: number;
  setBlocks: React.Dispatch<React.SetStateAction<ITrainingSet[][]>>;
  dragActive: boolean;
}) {
  const { blockIndex, insertBeforeRowIndex, setBlocks, dragActive } = props;
  const { classes } = useStyles();
  const theme = useTheme();
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: [TRAINING_SET_SORT_ROW],
      drop: (item: IRowDragItem) => {
        setBlocks((prev) =>
          moveRowToBlock(
            prev,
            item.blockIndex,
            item.rowIndex,
            blockIndex,
            insertBeforeRowIndex,
          ),
        );
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    }),
    [blockIndex, insertBeforeRowIndex, setBlocks],
  );

  const showTarget = dragActive && canDrop;
  const isHover = dragActive && isOver && canDrop;

  const innerHeight = !dragActive ? 4 : !showTarget ? 4 : isHover ? 56 : 32;

  return (
    <Box className={classes.dropZoneOuter}>
      <Box
        ref={drop as unknown as React.Ref<HTMLDivElement>}
        className={classes.dropZoneInner}
        sx={{
          minHeight: innerHeight,
          border: showTarget
            ? `2px dashed ${alpha(
                theme.palette.primary.main,
                isHover ? 0.92 : 0.58,
              )}`
            : 'none',
          bgcolor:
            showTarget && isHover
              ? alpha(theme.palette.primary.main, UiAlpha.Tint)
              : showTarget
                ? alpha(theme.palette.primary.main, UiAlpha.Subtle)
                : 'transparent',
        }}
      />
    </Box>
  );
}

function DropMergeOnBlock(props: {
  targetBlockIndex: number;
  setBlocks: React.Dispatch<React.SetStateAction<ITrainingSet[][]>>;
  dragActive: boolean;
  inline?: boolean;
}) {
  const { targetBlockIndex, setBlocks, dragActive, inline } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: [TRAINING_SET_SORT_ROW, TRAINING_SET_SORT_BLOCK],
      canDrop: (item: IRowDragItem | IBlockDragItem) => {
        if (item.type === TRAINING_SET_SORT_BLOCK) {
          return item.blockIndex !== targetBlockIndex;
        }
        return true;
      },
      drop: (item: IRowDragItem | IBlockDragItem) => {
        setBlocks((prev) => {
          if (item.type === TRAINING_SET_SORT_ROW) {
            return mergeRowIntoBlockEnd(
              prev,
              item.blockIndex,
              item.rowIndex,
              targetBlockIndex,
            );
          }
          return mergeBlocks(prev, item.blockIndex, targetBlockIndex);
        });
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    }),
    [targetBlockIndex, setBlocks],
  );

  const showTarget = dragActive && canDrop;
  const isHover = dragActive && isOver && canDrop;

  const mergeMinHeight = (() => {
    if (inline) {
      const idleInline = theme.spacing(4);
      if (!dragActive || !showTarget) {
        return idleInline;
      }
      return isHover ? theme.spacing(7) : theme.spacing(5.5);
    }
    if (!dragActive || !showTarget) {
      return theme.spacing(5.5);
    }
    return isHover ? theme.spacing(9) : theme.spacing(7);
  })();

  return (
    <Box
      className={clsx(
        classes.dropZoneOuter,
        inline && classes.dropZoneOuterInline,
      )}>
      <Box
        ref={drop as unknown as React.Ref<HTMLDivElement>}
        className={clsx(classes.mergeZone, inline && classes.mergeZoneInline)}
        sx={{
          boxSizing: 'border-box',
          minHeight: mergeMinHeight,
          border: showTarget
            ? `2px dashed ${alpha(
                theme.palette.primary.main,
                isHover ? 0.92 : 0.58,
              )}`
            : 'none',
          bgcolor:
            showTarget && isHover
              ? alpha(theme.palette.primary.main, UiAlpha.Tint)
              : showTarget
                ? alpha(theme.palette.primary.main, UiAlpha.Subtle)
                : 'transparent',
        }}>
        <Typography
          variant="caption"
          color="text.secondary"
          textAlign={inline ? 'left' : 'center'}
          sx={{
            fontSize: inline ? '0.7rem' : '0.75rem',
            lineHeight: 1.25,
            width: inline ? 'auto' : undefined,
            alignSelf: inline ? 'flex-start' : undefined,
          }}>
          {t('trainings.sortSetsMergeHint')}
        </Typography>
      </Box>
    </Box>
  );
}

function SortSetsDragLayer() {
  const { classes } = useStyles();
  const theme = useTheme();

  const { itemType, item, isDragging, clientOffset } = useDragLayer(
    (monitor) => ({
      item: monitor.getItem(),
      itemType: monitor.getItemType(),
      isDragging: monitor.isDragging(),
      clientOffset: monitor.getClientOffset(),
    }),
  );

  if (!isDragging || !clientOffset) {
    return null;
  }

  if (
    itemType !== TRAINING_SET_SORT_ROW &&
    itemType !== TRAINING_SET_SORT_BLOCK
  ) {
    return null;
  }

  const offsetX = clientOffset.x + 10;
  const offsetY = clientOffset.y + 10;

  return (
    <Box
      sx={{
        position: 'fixed',
        left: 0,
        top: 0,
        pointerEvents: 'none',
        zIndex: theme.zIndex.modal + 2,
        transform: `translate(${offsetX}px, ${offsetY}px)`,
      }}>
      {itemType === TRAINING_SET_SORT_ROW ? (
        <Box
          className={classes.dragLayerCard}
          sx={() => {
            const w = (item as IRowDragItem).previewWidth;
            return w
              ? {
                  width: w,
                  minWidth: w,
                  maxWidth: 'none',
                  boxSizing: 'border-box',
                }
              : {};
          }}>
          <AppExerciseAvatar
            exercise={(item as IRowDragItem).previewExercise}
            size={AvatarSize.s}
          />
          <Typography variant="h3" flex={1} noWrap>
            {(item as IRowDragItem).previewTitle}
          </Typography>
        </Box>
      ) : (
        <Box
          className={classes.dragLayerCard}
          sx={() => {
            const w = (item as IBlockDragItem).previewWidth;
            return w
              ? {
                  width: w,
                  minWidth: w,
                  maxWidth: 'none',
                  boxSizing: 'border-box',
                }
              : {};
          }}>
          <DragIndicatorIcon color="primary" fontSize="medium" />
          <Typography variant="h3" flex={1} noWrap>
            {(item as IBlockDragItem).previewLabel}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function DraggableExerciseRow(props: {
  trainingSet: ITrainingSet;
  blockIndex: number;
  rowIndex: number;
}) {
  const { trainingSet, blockIndex, rowIndex } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();
  const title = trainingSet.exercise?.title || t('trainings.training');
  const rowRef = useRef<HTMLDivElement | null>(null);

  const [{ isDragging }, drag, dragPreview] = useDrag(
    () => ({
      type: TRAINING_SET_SORT_ROW,
      item: () => ({
        type: TRAINING_SET_SORT_ROW,
        blockIndex,
        rowIndex,
        previewTitle: title,
        previewExercise: trainingSet.exercise,
        previewWidth: rowRef.current?.getBoundingClientRect().width,
      }),
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [blockIndex, rowIndex, title, trainingSet.exercise],
  );

  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, [dragPreview]);

  return (
    <Box
      ref={rowRef}
      className={clsx(
        classes.row,
        rowIndex > 0 && classes.rowAfterFirst,
        isDragging && classes.rowDragging,
      )}
      sx={{ opacity: isDragging ? 0.5 : 1 }}>
      <Box
        ref={drag as unknown as React.Ref<HTMLDivElement>}
        className={classes.rowDragHandle}
        aria-hidden>
        <DragIndicatorIcon fontSize="medium" />
      </Box>
      <AppExerciseAvatar exercise={trainingSet.exercise} size={AvatarSize.s} />
      <Typography variant="h3" flex={1}>
        {title}
      </Typography>
    </Box>
  );
}

function DraggableBlockHeader(props: { blockIndex: number; enabled: boolean }) {
  const { blockIndex, enabled } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();
  const previewLabel = t('trainings.sortSetsBlockHandle');
  const dragHandleRef = useRef<HTMLDivElement | null>(null);

  const [{ isDragging }, drag, dragPreview] = useDrag(
    () => ({
      type: TRAINING_SET_SORT_BLOCK,
      item: () => {
        const blockEl = dragHandleRef.current?.closest(
          '[data-sort-training-block]',
        ) as HTMLElement | null;
        return {
          type: TRAINING_SET_SORT_BLOCK,
          blockIndex,
          previewLabel,
          previewWidth: blockEl?.getBoundingClientRect().width,
        };
      },
      canDrag: () => enabled,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [blockIndex, enabled, previewLabel],
  );

  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, [dragPreview]);

  if (!enabled) {
    return null;
  }

  function setDragHandleRef(node: HTMLDivElement | null) {
    dragHandleRef.current = node;
    (drag as (n: HTMLDivElement | null) => void)(node);
  }

  return (
    <Box
      ref={setDragHandleRef}
      className={clsx(
        classes.blockDragHandle,
        isDragging && classes.blockDragHandleDragging,
      )}
      sx={{ opacity: isDragging ? 0.5 : 1 }}
      aria-label={t('trainings.sortSetsDragBlock')}>
      <DragIndicatorIcon fontSize="medium" />
    </Box>
  );
}

export default function SortTrainingSetsModal(
  props: ISortTrainingSetsModalProps,
) {
  const { open, onClose, trainingSets, onSave, isSaving } = props;
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [blocks, setBlocks] = useState<ITrainingSet[][]>([]);
  const listRef = useRef<HTMLDivElement | null>(null);
  const scrollVelocityRef = useRef(0);
  const scrollRafRef = useRef<number | null>(null);

  const { isDragging: dragActive, clientOffset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    clientOffset: monitor.getClientOffset(),
  }));

  useEffect(() => {
    if (open && trainingSets) {
      setBlocks(trainingSetsToBlocks(trainingSets));
    }
  }, [open, trainingSets]);

  useEffect(() => {
    function stopAutoScroll() {
      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
      scrollVelocityRef.current = 0;
    }

    if (!open || !dragActive || !clientOffset || !listRef.current) {
      stopAutoScroll();
      return;
    }

    const container = listRef.current;
    const rect = container.getBoundingClientRect();
    const edgeThresholdPx = 56;
    const maxSpeedPxPerFrame = 14;

    let nextVelocity = 0;
    const topDistance = clientOffset.y - rect.top;
    const bottomDistance = rect.bottom - clientOffset.y;

    if (topDistance < edgeThresholdPx) {
      const ratio = Math.max(
        0,
        (edgeThresholdPx - topDistance) / edgeThresholdPx,
      );
      nextVelocity = -maxSpeedPxPerFrame * ratio;
    } else if (bottomDistance < edgeThresholdPx) {
      const ratio = Math.max(
        0,
        (edgeThresholdPx - bottomDistance) / edgeThresholdPx,
      );
      nextVelocity = maxSpeedPxPerFrame * ratio;
    }

    scrollVelocityRef.current = nextVelocity;

    if (scrollVelocityRef.current !== 0 && scrollRafRef.current === null) {
      const tick = () => {
        const velocity = scrollVelocityRef.current;
        if (!container || velocity === 0) {
          scrollRafRef.current = null;
          return;
        }

        const prevTop = container.scrollTop;
        container.scrollTop += velocity;
        const reachedLimit = prevTop === container.scrollTop;

        if (reachedLimit) {
          scrollRafRef.current = null;
          return;
        }

        scrollRafRef.current = requestAnimationFrame(tick);
      };

      scrollRafRef.current = requestAnimationFrame(tick);
    }

    if (scrollVelocityRef.current === 0 && scrollRafRef.current !== null) {
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }

    return () => {
      if (!dragActive) {
        stopAutoScroll();
      }
    };
  }, [open, dragActive, clientOffset]);

  async function handleSave() {
    await onSave(blocksToOrderPayload(blocks));
  }

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={t('trainings.sortSetsTitle')}>
      <Box className={classes.modalDndSurface}>
        <Typography
          variant="body1"
          color="text.secondary"
          marginBottom={2}
          sx={{ whiteSpace: 'pre-line', lineHeight: 1.55 }}>
          {t('trainings.sortSetsDescription')}
        </Typography>
        <Box ref={listRef} className={classes.list}>
          <DropBetweenBlocks
            insertBeforeBlockIndex={0}
            setBlocks={setBlocks}
            dragActive={dragActive}
          />
          {blocks.map((block, blockIndex) => (
            <React.Fragment key={block.map((s) => s.id).join('-')}>
              <Box className={classes.block} data-sort-training-block>
                {block.length > 1 ? (
                  <Box className={classes.blockHeaderRow}>
                    <DraggableBlockHeader blockIndex={blockIndex} enabled />
                    <DropMergeOnBlock
                      targetBlockIndex={blockIndex}
                      setBlocks={setBlocks}
                      dragActive={dragActive}
                      inline
                    />
                  </Box>
                ) : (
                  <DropMergeOnBlock
                    targetBlockIndex={blockIndex}
                    setBlocks={setBlocks}
                    dragActive={dragActive}
                  />
                )}
                {block.map((trainingSet, rowIndex) => (
                  <React.Fragment key={getSetKey(trainingSet)}>
                    <DropBeforeRow
                      blockIndex={blockIndex}
                      insertBeforeRowIndex={rowIndex}
                      setBlocks={setBlocks}
                      dragActive={dragActive}
                    />
                    <DraggableExerciseRow
                      trainingSet={trainingSet}
                      blockIndex={blockIndex}
                      rowIndex={rowIndex}
                    />
                  </React.Fragment>
                ))}
              </Box>
              <DropBetweenBlocks
                insertBeforeBlockIndex={blockIndex + 1}
                setBlocks={setBlocks}
                dragActive={dragActive}
              />
            </React.Fragment>
          ))}
        </Box>
        <Box className={classes.footer}>
          <AppCancelButton onClick={onClose} disabled={isSaving}>
            {t('common.cancel')}
          </AppCancelButton>
          <AppButton loading={isSaving} onClick={handleSave}>
            {t('common.save')}
          </AppButton>
        </Box>
      </Box>
      {open ? <SortSetsDragLayer /> : null}
    </AppDialog>
  );
}
